import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

// ========================================
// Portal Authentication Types
// ========================================

export interface PortalLoginInitiateRequest {
  tenantId?: string;
  tenantSlug?: string;
  identifier: string;  // phone or email
}

export interface PortalLoginInitiateResponse {
  success: boolean;
  message: string;
  sessionToken: string;
  maskedIdentifier: string;
  otpValiditySeconds: number;
  resendCooldownSeconds: number;
  resendsRemaining: number;
  tenantId?: string;
  tenantName?: string;
}

export interface PortalOtpVerifyRequest {
  sessionToken: string;
  otp: string;
}

export interface PortalOtpVerifyResponse {
  success: boolean;
  message: string;
  questionType?: string;  // DOB, PINCODE, CUSTOM - null if OTP-only mode
  questionLabel?: string;
  verificationToken?: string;
  dataOwnerFirstName?: string;
  
  // OTP-only mode fields (when no security question is required)
  otpOnlyMode?: boolean;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  dataOwner?: {
    dataOwnerId: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
  };
  tenant?: {
    tenantId: string;
    tenantSlug: string;
    tenantName: string;
    logo: string | null;
  };
}

export interface PortalSecurityAnswerRequest {
  verificationToken: string;
  answer: string;
}

export interface PortalLoginResponse {
  success: boolean;
  message: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  dataOwner: {
    dataOwnerId: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
  };
  tenant: {
    tenantId: string;
    tenantSlug: string;
    tenantName: string;
    logo: string | null;
  };
}

// ========================================
// Portal Data Types
// ========================================

export interface ConsentHistoryItem {
  consentId: string;
  tenantId: string;
  status: string;  // ACTIVE, WITHDRAWN, EXPIRED, PENDING
  reconsentStatus?: string;
  projectName: string;
  templateName: string;
  dataCollectorName?: string;
  evidenceType?: string;
  submittedAt?: string;
  expiresAt?: string;
  withdrawnAt?: string;
  dataCategories?: string[];
  processingPurposes?: string[];
  canWithdraw: boolean;
  hasPendingWithdrawal: boolean;
}

export interface ConsentDetail extends ConsentHistoryItem {
  retentionPeriod?: string;
  thirdPartySharing?: boolean;
}

export interface WithdrawalHistoryItem {
  withdrawalId: string;
  consentId: string;
  status: string;  // PENDING, APPROVED, REJECTED, PROCESSING
  reason?: string;
  requestedAt?: string;
  processedAt?: string;
  rejectionReason?: string;
  projectName?: string;
  templateName?: string;
}

export interface CreateWithdrawalRequest {
  consentId: string;
  reason?: string;
}

export interface CreateWithdrawalResponse {
  requestId: string;
  status: string;
  message: string;
}

// ========================================
// Portal Authentication APIs
// ========================================

// Backend wraps responses in ApiResponse: { success, data, error, timestamp }
interface ApiResponse<T> {
  success: boolean;
  data: T;
  error: string | null;
  timestamp: number;
}

/**
 * Initiate portal login - sends OTP to the data owner's phone/email.
 */
export async function initiatePortalLogin(request: PortalLoginInitiateRequest): Promise<PortalLoginInitiateResponse> {
  const response = await axios.post<ApiResponse<PortalLoginInitiateResponse>>(
    `${API_BASE_URL}/portal/v1/auth/initiate`,
    request,
    { headers: { 'Content-Type': 'application/json' } }
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to initiate login');
  }
  return response.data.data;
}

/**
 * Verify OTP and get security question.
 */
export async function verifyPortalOtp(request: PortalOtpVerifyRequest): Promise<PortalOtpVerifyResponse> {
  const response = await axios.post<ApiResponse<PortalOtpVerifyResponse>>(
    `${API_BASE_URL}/portal/v1/auth/verify-otp`,
    request,
    { headers: { 'Content-Type': 'application/json' } }
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to verify OTP');
  }
  return response.data.data;
}

/**
 * Verify security answer and complete login.
 */
export async function verifySecurityAnswer(request: PortalSecurityAnswerRequest): Promise<PortalLoginResponse> {
  const response = await axios.post<ApiResponse<PortalLoginResponse>>(
    `${API_BASE_URL}/portal/v1/auth/verify-answer`,
    request,
    { headers: { 'Content-Type': 'application/json' } }
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to verify security answer');
  }
  return response.data.data;
}

/**
 * Logout from portal session.
 * Note: Backend logout endpoint requires session ID which we don't track.
 * For now, just clear local session. Backend session will expire naturally.
 */
export async function portalLogout(token: string): Promise<void> {
  // Just clear local session - backend sessions have TTL
  clearPortalSession();
}

// ========================================
// Portal Data APIs
// ========================================

/**
 * Get consent history for the logged-in data owner.
 */
export async function getConsentHistory(token: string, tenantId: string, dataOwnerId: string): Promise<ConsentHistoryItem[]> {
  const response = await axios.get<ApiResponse<ConsentHistoryItem[]>>(
    `${API_BASE_URL}/portal/v1/consents`,
    { headers: { 'Authorization': `Bearer ${token}`, 'X-Tenant-Id': tenantId, 'X-Data-Owner-Id': dataOwnerId } }
  );
  return response.data.data || [];
}

/**
 * Get consent detail by ID.
 */
export async function getConsentDetail(token: string, tenantId: string, dataOwnerId: string, consentId: string): Promise<ConsentDetail> {
  const response = await axios.get<ApiResponse<ConsentDetail>>(
    `${API_BASE_URL}/portal/v1/consents/${consentId}`,
    { headers: { 'Authorization': `Bearer ${token}`, 'X-Tenant-Id': tenantId, 'X-Data-Owner-Id': dataOwnerId } }
  );
  if (!response.data.data) {
    throw new Error('Consent not found');
  }
  return response.data.data;
}

/**
 * Get withdrawal request history.
 */
export async function getWithdrawalHistory(token: string, tenantId: string, dataOwnerId: string): Promise<WithdrawalHistoryItem[]> {
  const response = await axios.get<ApiResponse<WithdrawalHistoryItem[]>>(
    `${API_BASE_URL}/portal/v1/withdrawals`,
    { headers: { 'Authorization': `Bearer ${token}`, 'X-Tenant-Id': tenantId, 'X-Data-Owner-Id': dataOwnerId } }
  );
  return response.data.data || [];
}

/**
 * Create a new withdrawal request.
 */
export async function createWithdrawalRequest(
  token: string,
  tenantId: string,
  dataOwnerId: string,
  request: CreateWithdrawalRequest
): Promise<CreateWithdrawalResponse> {
  const response = await axios.post<ApiResponse<CreateWithdrawalResponse>>(
    `${API_BASE_URL}/portal/v1/withdrawals`,
    request,
    { 
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Tenant-Id': tenantId,
        'X-Data-Owner-Id': dataOwnerId
      } 
    }
  );
  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to create withdrawal request');
  }
  return response.data.data;
}

// ========================================
// Portal Session Management
// ========================================

const PORTAL_TOKEN_KEY = 'portal_token';
const PORTAL_REFRESH_TOKEN_KEY = 'portal_refresh_token';
const PORTAL_USER_KEY = 'portal_user';

export interface PortalUser {
  dataOwnerId: string;
  firstName: string;
  lastName: string;
  email: string;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  expiresAt: string;
}

/**
 * Store portal session after successful login.
 */
export function storePortalSession(response: PortalLoginResponse): void {
  localStorage.setItem(PORTAL_TOKEN_KEY, response.accessToken);
  localStorage.setItem(PORTAL_REFRESH_TOKEN_KEY, response.refreshToken);
  const expiresAt = new Date(Date.now() + response.expiresIn * 1000).toISOString();
  localStorage.setItem(PORTAL_USER_KEY, JSON.stringify({
    dataOwnerId: response.dataOwner.dataOwnerId,
    firstName: response.dataOwner.firstName,
    lastName: response.dataOwner.lastName,
    email: response.dataOwner.email,
    tenantId: response.tenant.tenantId,
    tenantSlug: response.tenant.tenantSlug,
    tenantName: response.tenant.tenantName,
    expiresAt: expiresAt,
  }));
}

/**
 * Get stored portal token.
 */
export function getPortalToken(): string | null {
  return localStorage.getItem(PORTAL_TOKEN_KEY);
}

/**
 * Get stored portal user info.
 */
export function getPortalUser(): PortalUser | null {
  const stored = localStorage.getItem(PORTAL_USER_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

/**
 * Check if portal session is valid (not expired).
 */
export function isPortalSessionValid(): boolean {
  const user = getPortalUser();
  if (!user) return false;
  return new Date(user.expiresAt) > new Date();
}

/**
 * Clear portal session.
 */
export function clearPortalSession(): void {
  localStorage.removeItem(PORTAL_TOKEN_KEY);
  localStorage.removeItem(PORTAL_REFRESH_TOKEN_KEY);
  localStorage.removeItem(PORTAL_USER_KEY);
}

export const portalApi = {
  initiateLogin: initiatePortalLogin,
  verifyOtp: verifyPortalOtp,
  verifySecurityAnswer,
  logout: portalLogout,
  getConsentHistory,
  getConsentDetail,
  getWithdrawalHistory,
  createWithdrawalRequest,
  storeSession: storePortalSession,
  getToken: getPortalToken,
  getUser: getPortalUser,
  isSessionValid: isPortalSessionValid,
  clearSession: clearPortalSession,
};
